import {
  createContext,
  FC, PropsWithChildren, useContext, useMemo, useState,
} from 'react';
import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import { getInitialValues } from 'src/trip-cart/utils/tripCartInitialValues';

type MotivatorsContext = {
  motivatorsDates: Date[];
  setMotivatorsDates: (dates: Date[]) => void;
  listingBlockedTimes: BlockedTime[];
  setListingBlockedTimes: (times: BlockedTime[]) => void;
  isListingMultiDayState: boolean;
  setIsListingMultiDayState: (isMultiDay: boolean) => void;
};

type Props = {
  listing: FullBoatSearchDetail;
  tripCartCookieData?: Partial<RecentInquiryData>;
};

const ListingMotivatorsContext = createContext<MotivatorsContext | null>(null);

const ListingMotivatorsProvider: FC<PropsWithChildren<Props>> = ({
  children,
  listing,
  tripCartCookieData,
}) => {
  const { formSelectedDates, isMultiDay } = getInitialValues({
    listing,
    bookingMotivators: listing.booking_motivators,
    tripCartCookieData,
  });
  const [motivatorsDates, setMotivatorsDates] = useState<Date[]>(formSelectedDates);
  const [listingBlockedTimes, setListingBlockedTimes] = useState<BlockedTime[]>([]);
  const [isListingMultiDayState, setIsListingMultiDayState] = useState(isMultiDay);

  const value = useMemo(() => ({
    motivatorsDates,
    setMotivatorsDates,
    listingBlockedTimes,
    setListingBlockedTimes,
    isListingMultiDayState,
    setIsListingMultiDayState,
  }), [motivatorsDates, listingBlockedTimes, isListingMultiDayState, setIsListingMultiDayState]);

  return (
    <ListingMotivatorsContext.Provider value={value}>
      {children}
    </ListingMotivatorsContext.Provider>
  );
};

export default ListingMotivatorsProvider;

export const useListingMotivators = (): MotivatorsContext => {
  const context = useContext(ListingMotivatorsContext);

  if (!context) {
    throw new Error('useListingMotivators must be used within a ListingMotivatorsProvider');
  }

  return context;
};
